import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandAlphabetLetterModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { VerticalColumnCellProps } from "@insite/content-library/Components/VerticalColumnCell";
import { VerticalColumnContainerProps } from "@insite/content-library/Components/VerticalColumnContainer";
import BrandAlphabetNavigation, {
    BrandAlphabetNavigationStyles,
} from "@insite/content-library/Widgets/Brand/BrandAlphabetNavigation";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showBrandNames = "showBrandNames",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showBrandNames]: boolean;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    allBrandsDataView: getAllBrandsDataView(state),
    brandAlphabetState: state.pages.brands.brandAlphabetState,
    featuredBrandIds: props.fields.brandsSelected,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

type BrandLetterMap = {
    [letter: string]: BrandModel[];
};

export interface BrandListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    brandAlphabetNavigation?: BrandAlphabetNavigationStyles;
    brandAccordion?: AccordionProps;
    brandAccordionSection?: AccordionSectionPresentationProps;
    verticalColumnContainer?: VerticalColumnContainerProps;
    verticalColumnCell?: VerticalColumnCellProps;
    brandLink?: LinkPresentationProps;
    actions?: InjectableCss;
    actionLinks?: LinkPresentationProps;
    actionLinksDisabled?: TypographyPresentationProps;
    buttons?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    innerContainer?: GridContainerProps;
    brandItem?: GridItemProps;
    header?: InjectableCss;
    clickableCard?: InjectableCss;
    brandImage?: LazyImageProps;
    mobileSelectBrandLetter?: SelectPresentationProps;
}

export const listStyles: BrandListStyles = {
    mobileSelectBrandLetter: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                padding-bottom: 15px;
                justify-content: center;
            `,
            formInputWrapper: css`
                width: 45%;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    header: {
        css: css`
            text-align: center;
            font-weight: bold;
            margin-bottom: 20px;
        `,
    },
    innerContainer: {
        css: css`
            align-self: end;
        `,
        gap: 0,
    },
    clickableCard: {
        css: css`
            padding: 20px 20px 20px 20px;
            background-color: whitesmoke;
            justify-content: center;
            display: flex;
            flex-direction: column;
            height: 200px;
            width: 200px;
            margin-bottom: 20px;
            & :hover {
                box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);
            }
        `,
    },
    brandItem: {
        width: [12, 12, 4, 4, 3],
        css: css`
            justify-content: center;
            align-items: center;
        `,
    },
    brandImage: {
        css: css`
            margin: auto;
            max-width: 100%;
            max-height: 100%;
            height: auto;
            width: auto;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    brandAccordionSection: {
        headerProps: {
            css: css`
                button {
                    background: ${getColor("secondary")};
                }
            `,
        },
        toggleIconProps: {
            color: "white",
        },
        titleTypographyProps: {
            weight: "bold",
            color: "white",
            css: css`
                text-transform: uppercase;
                color: white;
            `,
        },
    },
    backToTopButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            margin: 10px;
        `,
    },
    brandLink: {
        css: css`
            width: auto;
            display: block;
            text-align: center;
            font-weight: bold;
            &:focus {
                outline-offset: -2px;
            }
        `,
    },
    verticalColumnContainer: {
        columnCounts: [2, 4, 4, 4, 4],
    },
    actions: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding: 5px;
        `,
    },
    actionLinks: {
        css: css`
            margin: 5px;
        `,
    },
    actionLinksDisabled: {
        css: css`
            margin: 5px;
        `,
        color: "text.disabled",
    },
    buttons: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
};

const styles = listStyles;

interface BrandListState {
    expanded: boolean | "mixed";
    brandLetterDetails: BrandAlphabetLetterModel[];
    brandSections: BrandSection[];
}

/**
 * This will display all Brands grouped by the first character.
 * Starting with numbers grouped into '#'.
 * @param props
 */
const CustomBrandList: FC<Props> = (props: Props) => {
    const { allBrandsDataView, brandAlphabetState, featuredBrandIds } = props;

    const brandList = allBrandsDataView.value || [];
    const brandAlphabet = brandAlphabetState.value || [];

    const { letters, brandLettersMap } = buildLettersAndMap(brandList);
    const { brandLetterDetails, brandSections } = buildBrandSections(letters, brandLettersMap, brandAlphabet, true);

    const [state, setState] = React.useState<BrandListState>({ expanded: true, brandLetterDetails, brandSections });
    const [featuredExpanded, toggleFeaturedExpanded] = useState(true);
    const [selectedValue, setSelectedValue] = useState("");

    React.useEffect(() => {
        setState({ ...state, brandLetterDetails, brandSections });
    }, [brandLetterDetails?.length, brandSections?.length]);

    if (allBrandsDataView.isLoading || brandAlphabetState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    if (brandList.length === 0) {
        return null;
    }

    const featuredBrands: BrandModel[] = [];
    if (featuredBrandIds && featuredBrandIds.length > 0) {
        featuredBrandIds.forEach(id => {
            const brand = brandList.find(brand => brand.id === id);
            if (brand) {
                featuredBrands.push(brand);
            }
        });
    }

    const featuredBrandSection = {
        letter: "Featured Brands",
        brandMap: featuredBrands,
        expanded: true,
    };

    brandSections.unshift(featuredBrandSection);

    const onClickFeaturedSection = () => () => {
        toggleFeaturedExpanded(!featuredExpanded);
    };

    /**
     * Regardless of how the BrandSection was toggled, this is a generic function that will determine if
     * the state.expanded value should be true, false or 'mixed' based on the state of the BrandSection controls
     * in BrandSections.
     */
    const determineNextExpandedValue = (
        expanded: boolean | "mixed",
        section: BrandSection,
        brandSections: BrandSection[],
    ) => {
        const nextSectionExpanded = !section.expanded;
        let nextExpanded = expanded;
        const allToggledItems = brandSections.filter(item => {
            return item.letter !== "Featured Brands";
        });
        const allExpandedItems = allToggledItems.filter(item => {
            return item.expanded === true;
        });
        if (allExpandedItems.length === allToggledItems.length) {
            nextExpanded = true;
        } else if (allExpandedItems.length === 0) {
            nextExpanded = false;
        } else {
            nextExpanded = "mixed";
        }
        return nextExpanded;
    };

    /**
     * If the user selects a BrandSection from either the BrandAlphabetNavigation or Select controls,
     * the expanded value of the newSectionState should always be "true" so it will display expanded.
     */
    const onSelectBrandSection = (section: BrandSection) => () => {
        setState(({ brandSections, expanded, brandLetterDetails }: BrandListState) => {
            const itemIndex = brandSections.findIndex(item => item.letter === section.letter);
            const newSectionState = { ...section, expanded: true };
            const newBrandSections = [...brandSections];
            newBrandSections.splice(itemIndex, 1, newSectionState);
            const nextExpanded = determineNextExpandedValue(expanded, section, newBrandSections);
            return { brandSections: newBrandSections, expanded: nextExpanded, brandLetterDetails };
        });
    };

    /**
     * If the user selects a BrandSection by toggling the icons directly on the Accordian control,
     * the expanded value should toggle naturally.
     */
    const onClickBrandSection = (section: BrandSection) => () => {
        setState(({ brandSections, expanded, brandLetterDetails }: BrandListState) => {
            const itemIndex = brandSections.findIndex(item => item.letter === section.letter);
            const nextSectionExpanded = !section.expanded;
            const newSectionState = { ...section, expanded: nextSectionExpanded };
            const newBrandSections = [...brandSections];
            newBrandSections.splice(itemIndex, 1, newSectionState);
            const nextExpanded = determineNextExpandedValue(expanded, section, newBrandSections);
            return { brandSections: newBrandSections, expanded: nextExpanded, brandLetterDetails };
        });
    };

    const onCollapse = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (state.expanded === "mixed" || state.expanded) {
            const { letters, brandLettersMap } = buildLettersAndMap(brandList);
            const { brandLetterDetails, brandSections } = buildBrandSections(
                letters,
                brandLettersMap,
                brandAlphabet,
                false,
            );
            const featuredBrandSection = {
                letter: "Featured Brands",
                brandMap: featuredBrands,
                expanded: true,
            };

            brandSections.unshift(featuredBrandSection);
            setState({ ...state, brandLetterDetails, brandSections, expanded: false });
        }
    };

    const onExpand = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (state.expanded === "mixed" || !state.expanded) {
            const { letters, brandLettersMap } = buildLettersAndMap(brandList);
            const { brandLetterDetails, brandSections } = buildBrandSections(
                letters,
                brandLettersMap,
                brandAlphabet,
                true,
            );

            const featuredBrandSection = {
                letter: "Featured Brands",
                brandMap: featuredBrands,
                expanded: true,
            };

            brandSections.unshift(featuredBrandSection);
            setState({ ...state, brandLetterDetails, brandSections, expanded: true });
        }
    };

    const handleBackToTop = () => {
        window.scrollTo(0, 0);
        const elementToFocus = document.getElementById("collapseButton");
        if (elementToFocus) {
            elementToFocus.focus();
        } else {
            document.getElementById("expandButton")?.focus();
        }
    };

    /**
     * This is the handler for the BrandAlphabetNavigation control.
     */
    const handleBrandLetterClick = (letter: string) => {
        const section = state.brandSections.find(item => item.letter === letter);
        section && onSelectBrandSection(section)();
        const elem = document?.querySelector(`[data-test-key=${letter}]`);
        elem?.scrollIntoView(true);
    };

    /**
     * This is the handler for the Select control.
     */
    const handleSelectBrandLetterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedValue(event.target.value);
        const section = state.brandSections.find(item => item.letter === event.target.value);
        section && onSelectBrandSection(section)();
        const elem = document?.querySelector(`[data-test-key=${event.target.value}]`);
        elem?.scrollIntoView(true);
        setSelectedValue("");
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandList">
            <Hidden below="md">
                <BrandAlphabetNavigation
                    letterDetails={brandLetterDetails}
                    extendedStyles={styles.brandAlphabetNavigation}
                    onBrandLetterClick={handleBrandLetterClick}
                />
            </Hidden>
            <Hidden above="sm">
                <Select
                    {...styles.mobileSelectBrandLetter}
                    data-test-selector="mobileSelectBrandLetter"
                    onChange={handleSelectBrandLetterChange}
                    placeholder="Select"
                    value={selectedValue}
                >
                    <option key="header" value="header">
                        {translate("Skip to section")}
                    </option>
                    {letters.map(letter => (
                        <option key={letter} value={letter}>
                            &nbsp;{letter.toUpperCase()}&nbsp;
                        </option>
                    ))}
                </Select>
            </Hidden>
            <StyledWrapper {...styles.actions}>
                {state.expanded === false ? (
                    <Typography {...styles.actionLinksDisabled}>{translate("Collapse All")}</Typography>
                ) : (
                    <Link
                        onClick={onCollapse}
                        {...styles.actionLinks}
                        id="collapseButton"
                        data-test-selector="brandListCollapseAllLink"
                    >
                        {translate("Collapse All")}
                    </Link>
                )}
                {state.expanded === true ? (
                    <Typography {...styles.actionLinksDisabled}>{translate("Expand All")}</Typography>
                ) : (
                    <Link
                        onClick={onExpand}
                        {...styles.actionLinks}
                        id="expandButton"
                        data-test-selector="brandListExpandAllLink"
                    >
                        {translate("Expand All")}
                    </Link>
                )}
            </StyledWrapper>
            <Accordion headingLevel={2}>
                {state.brandSections.map(brandDetails => (
                    <AccordionSection
                        key={brandDetails.letter}
                        {...styles.brandAccordionSection}
                        title={brandDetails.letter}
                        expanded={brandDetails.expanded}
                        onTogglePanel={onClickBrandSection(brandDetails)}
                    >
                        <GridContainer
                            id={`letter-${brandDetails.letter}`}
                            {...styles.innerContainer}
                            data-test-selector="brandSection"
                        >
                            {brandDetails.brandMap.map(brand => (
                                <GridItem key={brand.detailPagePath} {...styles.brandItem}>
                                    <Clickable href={brand.detailPagePath} {...styles.clickableCard}>
                                        <LazyImage
                                            {...styles.brandImage}
                                            src={brand.logoSmallImagePath}
                                            altText={
                                                brand.logoAltText
                                                    ? `${translate("Go to brand page")} ${brand.logoAltText}`
                                                    : `${translate("Go to brand page")} ${brand.name}`
                                            }
                                        />
                                        {props.fields.showBrandNames && (
                                            <Typography {...styles.brandLink} data-test-selector="brandLink">
                                                {brand.name}
                                            </Typography>
                                        )}
                                    </Clickable>
                                </GridItem>
                            ))}
                        </GridContainer>
                        <StyledWrapper {...styles.actions}>
                            <Link onClick={handleBackToTop} {...styles.actionLinks}>
                                {translate("Back to Top")}
                            </Link>
                        </StyledWrapper>
                    </AccordionSection>
                ))}
            </Accordion>
            <StyledWrapper {...styles.buttons}>
                <Button {...styles.backToTopButton} onClick={handleBackToTop}>
                    {translate("Back to Top")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CustomBrandList),
    definition: {
        group: "Brands",
        icon: "List",
        displayName: "Custom Brand List",
        allowedContexts: ["BrandsPage"],
        fieldDefinitions: [
            {
                name: fields.showBrandNames,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                displayName: "Show Brand Names",
                fieldType: "General",
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                fieldType: "General",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "General",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "General",
            },
            {
                name: fields.maximumNumberOfBrandsPerRow,
                displayName: "Maximum brands per row",
                editorTemplate: "DropDownField",
                defaultValue: "6",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                ],
                fieldType: "General",
                tooltip:
                    "Up to 6 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
            },
        ],
    },
};

export default widgetModule;

interface BrandSection {
    letter: string;
    brandMap: BrandModel[];
    expanded: boolean;
}

/**
 * Takes in a brand list and sorts it into #, A-Z, Other characters.
 *
 * @param brandList The list of brands that should be sorted.
 */
const buildLettersAndMap = (brandList: BrandModel[]): { letters: string[]; brandLettersMap: BrandLetterMap } => {
    let letters: string[] = [];
    let letter: string;
    let newLetters: string[] = letters;
    const brandLettersMap: BrandLetterMap = {};
    for (let i = 0; i < brandList.length; i = i + 1) {
        const brand = brandList[i];
        letter = brand.name[0] ? brand.name[0].toLowerCase() : "";
        if (isNumeric(letter)) {
            letter = "#";
        }
        if (!brandLettersMap[letter]) {
            brandLettersMap[letter] = [];
        }
        brandLettersMap[letter].push(brand);
        newLetters = [...newLetters, letter];
    }
    letters = newLetters.filter((letter, index, array) => array.indexOf(letter) === index);
    if (brandLettersMap["#"]) {
        brandLettersMap["#"].sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    return { letters, brandLettersMap };
};

/**
 * Take in the props and create easier to work with state objects.
 *
 * @param letters List of letters and special characters.
 * @param brandLettersMap Brand list sorted into letter lists.
 * @param brandAlphabet Letter count list.
 * @param expanded The expanded value to use if not undefined.
 */
const buildBrandSections = (
    letters: string[],
    brandLettersMap: BrandLetterMap,
    brandAlphabet: BrandAlphabetLetterModel[],
    expanded: boolean | undefined,
): {
    brandLetterDetails: {
        count: number;
        letter: string;
        linkable: boolean;
    }[];
    brandSections: {
        letter: string;
        brandMap: BrandModel[];
        expanded: boolean;
    }[];
} => {
    const brandLetterDetails = brandAlphabet.map(alphabetItem => ({
        ...alphabetItem,
        linkable: alphabetItem.count > 0,
    }));
    const brandSections = letters.map(letter => ({
        letter,
        brandMap: brandLettersMap[letter],
        expanded: expanded || false,
    }));

    return { brandLetterDetails, brandSections };
};
